/** Race viewport geometry and resources owned by one Election view. */
export function raceScrollOffset(target: HTMLElement): number {
  if (!window.getComputedStyle || !target.getBoundingClientRect) return 0;
  const tool = target.closest<HTMLElement>(".bsc-election__tool") ?? target;
  const style = window.getComputedStyle(tool);
  const header = Math.max(0, parseFloat(style.getPropertyValue("--header-offset")) || 0);
  let obstruction = header;
  const finder = tool.querySelector<HTMLElement>(".bsc-ballot-finder");
  const nav = tool.querySelector<HTMLElement>(".bsc-race-nav");
  const toggle = nav?.querySelector<HTMLElement>(".bsc-race-nav__toggle");
  // A Body's heading docks beneath the finder while its Races pass.
  const groupHead = (target.closest(".bsc-race-group") ?? tool.querySelector(".bsc-race-group:not([hidden])"))
    ?.querySelector<HTMLElement>(".bsc-race-group__head") ?? null;
  // Desktop nav is beside the ledger; only the mobile bar covers its column.
  const cap = tool.querySelector<HTMLElement>(".bsc-ledger-cap");
  for (const dock of [finder, toggle && window.getComputedStyle(toggle).display !== "none" ? nav : null, cap, groupHead]) {
    if (!dock || dock.hidden) continue;
    const dockStyle = window.getComputedStyle(dock);
    if (dockStyle.position !== "sticky" && dockStyle.position !== "fixed") continue;
    const top = parseFloat(dockStyle.top);
    obstruction = Math.max(obstruction, (Number.isFinite(top) ? top : header) + dock.getBoundingClientRect().height);
  }
  return obstruction + 12;
}

function raceTop(target: HTMLElement): number {
  const style = window.getComputedStyle(target.closest(".bsc-election__tool") ?? target);
  const currentHeader = parseFloat(style.getPropertyValue("--header-offset")) || 0;
  const fullHeader = parseFloat(style.getPropertyValue("--header-height")) || currentHeader;
  // The host may reveal its header after a jump. Reserve that space on arrival,
  // while the observer continues to use the currently visible obstruction.
  const offset = raceScrollOffset(target) + Math.max(0, fullHeader - currentHeader);
  return Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
}

let glide = 0;
/** A short, fast glide: it moves on the first frame (quartic ease-out), takes
 *  ~230ms for a nearby Race and at most 380ms for the longest jump, and gives way the
 *  moment the reader scrolls. Instant under reduced motion. Returns its duration. */
function glidePageTo(resolve: () => number): number {
  const token = ++glide;
  const start = window.scrollY;
  const destination = resolve();
  const distance = destination - start;
  const reduced = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced || Math.abs(distance) < 2 || typeof requestAnimationFrame === "undefined" || typeof performance === "undefined") {
    window.scrollTo({ top: destination, behavior: "instant" });
    return 0;
  }
  const duration = Math.round(Math.min(380, 200 + Math.sqrt(Math.abs(distance)) * 1.5));
  const stop = () => { if (token === glide) ++glide; };
  const interrupts = ["wheel", "touchstart", "keydown"] as const;
  interrupts.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
  const cleanup = () => interrupts.forEach((type) => window.removeEventListener(type, stop));
  const started = performance.now();
  const step = (now: number) => {
    if (token !== glide) { cleanup(); return; }
    const progress = Math.min(1, (now - started) / duration);
    const eased = 1 - Math.pow(1 - progress, 4);
    // "instant" overrides a host's scroll-behavior: smooth on <html>, frame by frame.
    window.scrollTo({ top: start + distance * eased, behavior: "instant" });
    if (progress < 1) { requestAnimationFrame(step); return; }
    cleanup();
    // Land exactly, in case the page moved under the glide (a header returning).
    const settled = resolve();
    if (Math.abs(settled - window.scrollY) > 1) window.scrollTo({ top: settled, behavior: "instant" });
  };
  requestAnimationFrame(step);
  return duration;
}

/** Scrolls a Race to the top of the reading area. `animate` glides there (a navigator
 *  click); otherwise it jumps (arriving from a link). Returns the glide's duration. */
export function scrollRaceIntoView(target: HTMLElement, { animate = false }: { animate?: boolean } = {}): number {
  if (!window.getComputedStyle || !target.getBoundingClientRect) {
    target.scrollIntoView({ block: "start" });
    return 0;
  }
  if (animate) return glidePageTo(() => raceTop(target));
  window.scrollTo({ top: raceTop(target), behavior: "instant" });
  return 0;
}

export function attachRaceScrollSpy({ ledger, signal, onCurrent }: {
  ledger: HTMLElement;
  signal: AbortSignal;
  onCurrent(slug: string | null): void;
}): { refresh(): void; suspendForScroll(target: HTMLElement, holdMs?: number): void } {
  const noop = { refresh() {}, suspendForScroll() {} };
  if (signal.aborted || typeof IntersectionObserver === "undefined" || typeof requestAnimationFrame === "undefined") return noop;
  let sections: HTMLElement[] = [];
  let observer: IntersectionObserver | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let frame: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let suspended: HTMLElement | null = null;
  // A glide fires scroll events (and may fire scrollend) on every frame; the selection
  // holds until it lands so the navigator does not flick through the Races it passes.
  let holdUntil = 0;
  let last: string | null = null;
  let margin = "";
  let generation = 0;
  const intersecting = new Set<HTMLElement>();
  const observedDocks = new WeakSet<Element>();
  const active = () => !signal.aborted && ledger.isConnected;
  const visible = (section: HTMLElement) => section.isConnected && !section.closest("[hidden]");
  function select(section: HTMLElement | undefined) {
    const slug = section?.dataset.raceSlug ?? null;
    if (slug !== last) { last = slug; onCurrent(slug); }
  }
  function finalRaceAtScrollLimit() {
    if (window.scrollY <= 0) return;
    if (window.scrollY + window.innerHeight < document.documentElement.scrollHeight - 2) return;
    const final = sections.filter(visible).at(-1);
    const rect = final?.getBoundingClientRect();
    return rect && rect.top < window.innerHeight && rect.bottom <= window.innerHeight + 2 ? final : undefined;
  }
  // A jump lands its Race below the line by the space reserved for a hidden host header
  // (scrollRaceIntoView). While that Race still sits in the reserved band, it stays
  // current: the Race above it only shows in the gap, and it is not what the reader chose.
  function holdsJumpTarget(line: number) {
    const held = last ? sections.find((section) => section.dataset.raceSlug === last && visible(section)) : undefined;
    if (!held) return false;
    const style = window.getComputedStyle(ledger.closest(".bsc-election__tool") ?? ledger);
    const currentHeader = parseFloat(style.getPropertyValue("--header-offset")) || 0;
    const fullHeader = parseFloat(style.getPropertyValue("--header-height")) || currentHeader;
    const reserve = Math.max(0, fullHeader - currentHeader);
    const rect = held.getBoundingClientRect();
    return reserve > 0 && rect.top >= line && rect.top <= line + reserve + 2;
  }
  function geometrySelection() {
    if (!active() || suspended) return;
    const shown = sections.filter(visible);
    if (!shown.length) { select(undefined); return; }
    const line = Math.min(raceScrollOffset(ledger), window.innerHeight - 1);
    if (holdsJumpTarget(line)) return;
    const bottom = Math.max(line + 1, window.innerHeight * 0.32);
    const winner = finalRaceAtScrollLimit() ?? shown.find((section) => {
      const rect = section.getBoundingClientRect();
      // 2px of slack: a jump lands its Race on the line to within a pixel, and the
      // Race above must not keep the last sliver.
      return rect.bottom > line + 2 && rect.top < bottom;
    });
    // A tall final Race remains current at the document's scroll limit.
    if (winner) select(winner);
    else if (!shown.some((section) => section.dataset.raceSlug === last)) select(shown[0]);
  }
  // The selection band starts under whatever is docked over the ledger. That moves
  // without a scroll or resize when the host header slides back in after a jump, so the
  // band is re-derived before any selection and on every transition end.
  function currentMargin() {
    const height = Math.max(1, window.innerHeight);
    const top = Math.min(raceScrollOffset(ledger), height - 1);
    const bottom = Math.min(height * 0.68, height - top - 1);
    return `-${top}px 0px -${bottom}px 0px`;
  }
  function rebuild(force = false) {
    if (!active()) return;
    // The ledger is constructed detached; discover docks after the mount.
    ledger.closest(".bsc-election__tool")?.querySelectorAll(".bsc-ballot-finder, .bsc-race-nav").forEach((dock) => {
      if (resizeObserver && !observedDocks.has(dock)) {
        observedDocks.add(dock);
        resizeObserver.observe(dock);
      }
    });
    sections = Array.from(ledger.querySelectorAll<HTMLElement>(".bsc-race"));
    const nextMargin = currentMargin();
    if (force || nextMargin !== margin) {
      margin = nextMargin;
      const version = ++generation;
      observer?.disconnect();
      intersecting.clear();
      observer = new IntersectionObserver((entries) => {
        if (!active() || version !== generation) return;
        if (currentMargin() !== margin) { rebuild(true); return; }
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          if (entry.isIntersecting && visible(el)) intersecting.add(el);
          else intersecting.delete(el);
        }
        if (!suspended) {
          const line = Math.min(raceScrollOffset(ledger), window.innerHeight - 1);
          if (holdsJumpTarget(line)) return;
          // The same 2px of slack as geometrySelection: a sliver left above the line is not current.
          const winner = finalRaceAtScrollLimit() ?? sections.find((section) =>
            intersecting.has(section) && visible(section) && section.getBoundingClientRect().bottom > line + 2);
          if (winner) select(winner);
          else geometrySelection();
        }
      }, { rootMargin: nextMargin });
      sections.filter(visible).forEach((section) => observer!.observe(section));
    }
    geometrySelection();
  }
  function schedule() {
    if (signal.aborted || frame !== null) return;
    frame = requestAnimationFrame(() => { frame = null; rebuild(); });
  }
  function resume() {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    suspended = null;
    schedule();
  }
  function keyInterrupt(event: Event) {
    if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes((event as KeyboardEvent).key)) resume();
  }
  const listeners: Array<[EventTarget, string, EventListener]> = [
    [window, "scroll", schedule], [window, "resize", schedule], [document, "transitionend", schedule],
    [window, "scrollend", () => { if (performance.now() >= holdUntil) resume(); }], [window, "wheel", resume],
    [window, "touchstart", resume], [window, "keydown", keyInterrupt],
  ];
  listeners.forEach(([target, event, listener]) => target.addEventListener(event, listener, { passive: true }));
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(schedule);
  }
  signal.addEventListener("abort", () => {
    ++generation;
    observer?.disconnect(); resizeObserver?.disconnect();
    listeners.forEach(([target, event, listener]) => target.removeEventListener(event, listener));
    if (frame !== null) cancelAnimationFrame(frame);
    if (timer !== null) clearTimeout(timer);
    frame = null; timer = null;
    intersecting.clear();
  }, { once: true });
  schedule();
  return {
    refresh() { if (active()) rebuild(true); else schedule(); },
    suspendForScroll(target, holdMs = 0) {
      if (signal.aborted) return;
      if (timer !== null) clearTimeout(timer);
      suspended = target;
      last = target.dataset.raceSlug ?? null;
      holdUntil = performance.now() + holdMs;
      timer = setTimeout(resume, Math.max(800, holdMs + 200));
    },
  };
}
